import { CornerDownLeftIcon, Loader2, X } from "lucide-react"
import React, { useEffect, useState } from "react"

interface TourTooltipProps {
  title: string
  description: string
  mockQuestion?: string
  showSendIcon?: boolean
  autoAdvanceDelay?: number
  onExit: () => void
  exitLabel: string
  currentStep?: number
}

export const TourTooltip: React.FC<TourTooltipProps> = ({
  title,
  description,
  mockQuestion,
  showSendIcon,
  autoAdvanceDelay,
  onExit,
  exitLabel,
  currentStep,
}) => {
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    if (!autoAdvanceDelay) return

    const startTime = Date.now()
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const newProgress = Math.min((elapsed / autoAdvanceDelay) * 100, 100)
      setProgress(newProgress)
    }, 50)

    return () => clearInterval(interval)
  }, [autoAdvanceDelay])

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {currentStep !== undefined && (
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
              {currentStep}
            </span>
          )}
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
        <button
          type="button"
          onClick={onExit}
          aria-label={exitLabel}
          className="border-zinc-500 bg-zinc-700 hover:bg-zinc-600 hover:border-zinc-400 shrink-0 rounded-full border p-1 transition-all"
        >
          <X className="text-zinc-300 h-3 w-3" />
        </button>
      </div>

      <p className="text-zinc-500 whitespace-pre-line text-sm leading-relaxed">
        {description}
        {showSendIcon && (
          <span className="ml-1 inline-flex items-center align-middle">
            <span className="inline-flex size-6 items-center justify-center rounded-sm bg-primary">
              <CornerDownLeftIcon className="size-3 text-primary-foreground" />
            </span>
          </span>
        )}
      </p>

      {mockQuestion && (
        <div className="mt-3 rounded-md border border-yellow1/30 bg-yellow1/10 p-3">
          <p className="text-sm italic text-white/90">"{mockQuestion}"</p>
        </div>
      )}

      {autoAdvanceDelay && (
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin text-yellow1" />
          </div>
          <div className="bg-zinc-700 h-1 w-full overflow-hidden rounded-full">
            <div
              className="h-full bg-linear-to-r from-yellow1/70 to-yellow1 transition-all duration-100 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
